/**
 * Sample data for running the app before a Supabase project exists.
 *
 * The leaderboard already had this; every screen added since needs it too, or half the
 * app is unreviewable until someone provisions a database. Read paths return fixtures.
 * Write paths throw `DemoError`, which the UI shows as "Sample mode — nothing is saved"
 * rather than pretending the write succeeded.
 *
 * One set of people, one set of ratings, one set of matches — everything else derives from
 * them. The board used to carry its own five players with their own ids, so tapping a row
 * led to a profile for somebody who did not exist. Anything a screen shows about a sample
 * player now comes from here, so the board and the profile behind it agree.
 */
import type { Game } from "@rally/rating";
import { SPORT } from "./sport.js";
import {
  deriveMatchCategory,
  matchRates,
  type AuctionPoolState,
  type AuctionRulesSetup,
  type RosterPlayer,
  type EventEntry,
  type Franchise,
  type Kind,
  type MatchStatus,
  type Lot,
  type MatchSummary,
  type Member,
  type PoolLots,
  type PoolSeeding,
  type SeedingEntry,
  type SeedingList,
  type Squad,
  type Tournament,
  type TournamentEvent,
} from "./types.js";

export class DemoError extends Error {
  constructor() {
    super("Sample mode — nothing is saved. Configure Supabase to log real matches.");
    this.name = "DemoError";
  }
}

/** The signed-in member in sample mode. Admin, so the console is reachable. */
export const demoMember: Member = {
  id: "demo-you",
  displayName: "You",
  handle: "you",
  avatarUrl: null,
  status: "active",
  role: "admin",
  selfAssessed: "club",
  category: "men",
  sports: [SPORT],
  dominantHand: "right",
  preferredSide: "left",
  createdAt: "2026-05-01T09:00:00.000Z",
  managed: false,
  junior: false,
  senior: false,
};

export const demoMembers: Member[] = [
  demoMember,
  member("demo-1", "Top seed", "active", "organiser", "men"),
  member("demo-2", "Second seed", "active", "player", "women"),
  member("demo-3", "Third seed", "active", "player", "men"),
  member("demo-4", "Fourth seed", "active", "player", "women"),
  member("demo-5", "New player", "active", "player", "women"),
  member("demo-6", "Waiting to join", "pending", "player", "men"),
  member("demo-7", "Also waiting", "pending", "player", "women"),
  // One with none set, because that is a real state and the picker has to handle it.
  member("demo-8", "Stepped away", "suspended", "player", null),
  // Your child: no login, a junior boy, and yours to enter in things. The register screen
  // has a second row because of them.
  { ...member("demo-kid-1", "Sample Kid", "active", "player", "men"), managed: true, junior: true },
];

export function demoMemberById(memberId: string): Member | null {
  return demoMembers.find((m) => m.id === memberId) ?? null;
}

/** Who looks after whom in sample mode — the `guardians` table, in miniature. */
const demoGuardianLinks = [{ memberId: "demo-kid-1", guardianId: "demo-you" }];

export function demoChildrenOf(guardianId: string): Member[] {
  return demoGuardianLinks
    .filter((link) => link.guardianId === guardianId)
    .flatMap((link) => demoMemberById(link.memberId) ?? []);
}

export function demoGuardiansOf(memberId: string): Member[] {
  return demoGuardianLinks
    .filter((link) => link.memberId === memberId)
    .flatMap((link) => demoMemberById(link.guardianId) ?? []);
}

/**
 * One rating per member per discipline they have played, which is what the real `ratings`
 * table holds. A discipline nobody has played has no row, so the profile can show the
 * empty slot it will really show.
 */
export interface DemoRating {
  memberId: string;
  kind: Kind;
  r: number;
  rd: number;
  matchesPlayed: number;
  /** Last match's movement, for the board. Absent where nothing has moved yet. */
  delta?: number;
}

export const demoRatings: DemoRating[] = [
  { memberId: "demo-1", kind: "doubles", r: 1720.8, rd: 42, matchesPlayed: 96, delta: 2.4 },
  { memberId: "demo-2", kind: "doubles", r: 1676.2, rd: 63, matchesPlayed: 71, delta: -8.6 },
  { memberId: "demo-you", kind: "doubles", r: 1612.4, rd: 58, matchesPlayed: 47, delta: 5.2 },
  { memberId: "demo-3", kind: "doubles", r: 1602.5, rd: 55, matchesPlayed: 84, delta: 1.1 },
  { memberId: "demo-4", kind: "doubles", r: 1548.0, rd: 88, matchesPlayed: 33 },
  // Two matches in and still being worked out — the provisional case the board splits out.
  { memberId: "demo-5", kind: "doubles", r: 1500.0, rd: 288, matchesPlayed: 2 },
  { memberId: "demo-1", kind: "singles", r: 1655.0, rd: 90, matchesPlayed: 20, delta: -3.4 },
  { memberId: "demo-3", kind: "singles", r: 1581.0, rd: 118, matchesPlayed: 9, delta: 3.4 },
  { memberId: "demo-you", kind: "singles", r: 1544.0, rd: 246, matchesPlayed: 4 },
];

export const demoMatches: MatchSummary[] = [
  demoMatch({
    id: "demo-match-1",
    kind: "doubles",
    status: "pending",
    games: [
      { a: 21, b: 18 },
      { a: 19, b: 21 },
      { a: 21, b: 15 },
    ],
    sideA: ["demo-1", "demo-3"],
    sideB: ["demo-you", "demo-2"],
    enteredBy: "demo-1",
    hoursAgo: 3,
  }),
  demoMatch({
    id: "demo-match-2",
    kind: "singles",
    status: "confirmed",
    games: [
      { a: 11, b: 7 },
      { a: 11, b: 9 },
    ],
    sideA: ["demo-you"],
    sideB: ["demo-4"],
    enteredBy: "demo-you",
    hoursAgo: 30,
  }),
  // From here down, matches you were not in. The Everyone filter is indistinguishable from
  // Mine without them, and a partner table needs somebody to have partnered somebody.
  demoMatch({
    id: "demo-match-3",
    kind: "doubles",
    status: "confirmed",
    games: [
      { a: 21, b: 14 },
      { a: 21, b: 19 },
    ],
    sideA: ["demo-1", "demo-3"],
    sideB: ["demo-2", "demo-4"],
    enteredBy: "demo-1",
    hoursAgo: 50,
  }),
  demoMatch({
    id: "demo-match-4",
    kind: "doubles",
    status: "confirmed",
    games: [
      { a: 21, b: 17 },
      { a: 21, b: 12 },
    ],
    sideA: ["demo-1", "demo-2"],
    sideB: ["demo-you", "demo-3"],
    enteredBy: "demo-2",
    hoursAgo: 74,
  }),
  demoMatch({
    id: "demo-match-5",
    kind: "doubles",
    status: "confirmed",
    games: [
      { a: 21, b: 16 },
      { a: 18, b: 21 },
      { a: 21, b: 13 },
    ],
    sideA: ["demo-you", "demo-1"],
    sideB: ["demo-3", "demo-4"],
    enteredBy: "demo-you",
    hoursAgo: 100,
  }),
  demoMatch({
    id: "demo-match-6",
    kind: "doubles",
    status: "void",
    games: [{ a: 21, b: 8 }],
    sideA: ["demo-2", "demo-5"],
    sideB: ["demo-3", "demo-4"],
    enteredBy: "demo-2",
    hoursAgo: 140,
  }),
  demoMatch({
    id: "demo-match-7",
    kind: "singles",
    status: "disputed",
    games: [
      { a: 21, b: 19 },
      { a: 21, b: 23 },
      { a: 21, b: 18 },
    ],
    sideA: ["demo-3"],
    sideB: ["demo-1"],
    enteredBy: "demo-3",
    hoursAgo: 160,
  }),
];

/**
 * Sample members alternate category, so the match-type filter on the log screen has
 * somebody to offer in each. With everyone uncategorised the filter worked correctly and
 * looked broken -- picking Women's emptied the list, which is right and unhelpful.
 */
function member(
  id: string,
  displayName: string,
  status: Member["status"],
  role: Member["role"],
  category: Member["category"] = "men",
): Member {
  return {
    id,
    displayName,
    handle: null,
    avatarUrl: null,
    status,
    role,
    selfAssessed: "casual",
    category,
    sports: [SPORT],
    dominantHand: null,
    preferredSide: null,
    createdAt: "2026-06-15T09:00:00.000Z",
    managed: false,
    junior: false,
    senior: false,
  };
}

/**
 * A sample match from the two things that are actually interesting about it — who played
 * and what the games were. Winner and category are derived here for the same reason the
 * database derives them: written by hand they drift, and a mixed match labelled Men's is a
 * bug that only ever shows up in a screenshot.
 */
function demoMatch(input: {
  id: string;
  kind: Kind;
  status: MatchStatus;
  games: Game[];
  sideA: string[];
  sideB: string[];
  enteredBy: string;
  hoursAgo: number;
}): MatchSummary {
  const gamesA = input.games.filter((game) => game.a > game.b).length;
  const winner = gamesA * 2 > input.games.length ? "A" : "B";
  const playedAt = hoursAgo(input.hoursAgo);
  const enteredSide = input.sideA.includes(input.enteredBy) ? "A" : "B";

  const category = deriveMatchCategory(
    [...input.sideA, ...input.sideB].map((id) => demoMemberById(id)?.category ?? null),
  );

  const participants = (["A", "B"] as const).flatMap((side) =>
    (side === "A" ? input.sideA : input.sideB).map((memberId) => ({
      memberId,
      displayName: demoMemberById(memberId)?.displayName ?? "Unknown",
      avatarUrl: null,
      side,
      // Whoever entered it agreed by entering it. A pending match is waiting on the other
      // side of the net and nobody there has answered yet.
      confirmedAt: input.status === "pending" && side !== enteredSide ? null : playedAt,
    })),
  );

  return {
    id: input.id,
    sport: SPORT,
    kind: input.kind,
    category,
    status: input.status,
    games: input.games,
    winner,
    playedAt,
    enteredBy: input.enteredBy,
    // Sample mode has a tournament but no draw and no results, so every match in it is
    // casual. When the sample gains a played event, this is the line that changes.
    context: "casual",
    // Casual, so the category is the whole rule — the same one the database applies.
    movesRatings: matchRates(category),
    event: null,
    participants,
  };
}

function hoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 3_600_000).toISOString();
}

/**
 * An entry list, built from the sample roster so the names match the board behind them.
 *
 * `hoursAgo` is what orders the waitlist, so the earlier number is the person nearer the
 * front — the sample would otherwise show a queue in an order the real one cannot produce.
 */
function entry(memberId: string, status: EventEntry["status"], hours: number): EventEntry {
  const who = demoMemberById(memberId);
  return {
    memberId,
    displayName: who?.displayName ?? "Someone",
    avatarUrl: who?.avatarUrl ?? null,
    status,
    requestedAt: hoursAgo(hours),
    seed: null,
  };
}

/**
 * Two events on one tournament: an open doubles round robin that is full and has a queue,
 * and an uncapped rating-bounded singles knockout you have not entered. Between them they
 * show every state the register button has — in, waiting, and not yet.
 *
 * Every event here is this sport's. `fetchTournaments` filters the real ones the same way,
 * and a sample that showed a pickleball row would be demonstrating a screen state this app
 * can no longer reach.
 */
export const demoEvents: TournamentEvent[] = [
  {
    id: "demo-event-1",
    tournamentId: "demo-tournament-1",
    name: "Open Doubles",
    sport: SPORT,
    kind: "doubles",
    format: "round_robin",
    category: "open",
    matchesPerPlayer: null,
    tiePairing: "mirrored",
    ordering: "rest",
    courts: 1,
    minRating: null,
    maxRating: null,
    capacity: 4,
    // Sample mode has no cutoff on either event: the screens that matter here are the
    // ones a first look should be able to press.
    entriesCloseAt: null,
    pairing: "snake",
    countsForRating: true,
    // Two paragraphs, so a first look shows the line break kept.
    description:
      "Starts 7pm sharp. Partners are drawn on the night.\n\nBring your own shuttles — feathers if you have them.",
    seedingClosedAt: null,
    lotsPublishedAt: null,
    squadFill: "auction",
    auctionFinishedAt: null,
    createdAt: "2026-07-01T09:00:00.000Z",
    entries: [
      entry("demo-1", "registered", 96),
      entry("demo-2", "registered", 92),
      entry("demo-3", "registered", 80),
      entry("demo-4", "registered", 74),
      // You left it too late, which is the state worth showing.
      entry("demo-you", "waitlist", 20),
      entry("demo-5", "waitlist", 12),
    ],
  },
  {
    id: "demo-event-2",
    tournamentId: "demo-tournament-1",
    name: "Intermediate Singles",
    sport: SPORT,
    kind: "singles",
    format: "knockout",
    category: "open",
    matchesPerPlayer: null,
    tiePairing: "mirrored",
    ordering: "seed_finale",
    courts: 1,
    minRating: 3.0,
    maxRating: 4.0,
    capacity: null,
    entriesCloseAt: null,
    pairing: "snake",
    countsForRating: true,
    description: null,
    seedingClosedAt: null,
    lotsPublishedAt: null,
    squadFill: "auction",
    auctionFinishedAt: null,
    createdAt: "2026-07-01T09:05:00.000Z",
    entries: [entry("demo-3", "registered", 60)],
  },
  // A kids' event: no rating, and your child is in it — the register screen's second row.
  {
    id: "demo-event-3",
    tournamentId: "demo-tournament-1",
    name: "Boys Singles",
    sport: SPORT,
    kind: "singles",
    format: "round_robin",
    category: "boys",
    matchesPerPlayer: null,
    tiePairing: "mirrored",
    ordering: "rest",
    courts: 1,
    minRating: null,
    maxRating: null,
    capacity: null,
    entriesCloseAt: null,
    pairing: "snake",
    countsForRating: false,
    description: "Under-12s. Parents, please stay courtside.",
    seedingClosedAt: null,
    lotsPublishedAt: null,
    squadFill: "auction",
    auctionFinishedAt: null,
    createdAt: "2026-07-01T09:10:00.000Z",
    entries: [entry("demo-kid-1", "registered", 40)],
  },
];

/**
 * A second sample tournament, one that picks its teams by auction, so the franchise screens
 * have something to show. Its two pools are empty: the auction fills them.
 */
const demoPools: TournamentEvent[] = [
  {
    id: "demo-pool-1",
    tournamentId: "demo-tournament-2",
    name: "Men's Doubles",
    sport: SPORT,
    kind: "doubles",
    format: "auction_pool",
    category: "men",
    matchesPerPlayer: null,
    tiePairing: "mirrored",
    ordering: "rest",
    courts: 1,
    minRating: null,
    maxRating: null,
    capacity: null,
    entriesCloseAt: null,
    pairing: "snake",
    countsForRating: true,
    description: null,
    seedingClosedAt: null,
    lotsPublishedAt: null,
    squadFill: "auction",
    auctionFinishedAt: null,
    createdAt: "2026-07-02T09:00:00.000Z",
    entries: [entry("demo-1", "registered", 50), entry("demo-2", "registered", 48)],
  },
  {
    id: "demo-pool-2",
    tournamentId: "demo-tournament-2",
    name: "Women's Doubles",
    sport: SPORT,
    kind: "doubles",
    format: "auction_pool",
    category: "women",
    matchesPerPlayer: null,
    tiePairing: "mirrored",
    ordering: "rest",
    courts: 1,
    minRating: null,
    maxRating: null,
    capacity: null,
    entriesCloseAt: null,
    pairing: "snake",
    countsForRating: true,
    description: null,
    seedingClosedAt: null,
    lotsPublishedAt: null,
    squadFill: "auction",
    auctionFinishedAt: null,
    createdAt: "2026-07-02T09:05:00.000Z",
    entries: [entry("demo-3", "registered", 40)],
  },
];

export const demoFranchises: Franchise[] = [
  {
    id: "demo-franchise-1",
    tournamentId: "demo-tournament-2",
    name: "Tigers",
    shortName: "TGR",
    logoUrl: null,
    colour: "#f97316",
    tagline: "Hear us roar",
  },
  {
    id: "demo-franchise-2",
    tournamentId: "demo-tournament-2",
    name: "Lions",
    shortName: null,
    logoUrl: null,
    colour: null,
    tagline: null,
  },
];

/** One squad per franchise per pool, with one captain named and the rest to appoint. */
export const demoSquads: Squad[] = demoFranchises.flatMap((franchise) =>
  demoPools.map((pool): Squad => {
    const captain =
      franchise.id === "demo-franchise-1" && pool.id === "demo-pool-1"
        ? demoMemberById("demo-1")
        : undefined;
    return {
      id: `demo-squad-${franchise.id}-${pool.id}`,
      franchiseId: franchise.id,
      eventId: pool.id,
      captain: captain
        ? { id: captain.id, displayName: captain.displayName, avatarUrl: captain.avatarUrl }
        : null,
      purse: 100,
    };
  }),
);

/**
 * A sample panel for the first auction pool: one panelist who is not registered in it, one who
 * is, and one with a draft. The average is of the two submitted lists, which is what an
 * organiser would see while seeding is still open.
 */
export function demoSeeding(eventId: string): PoolSeeding {
  if (eventId !== "demo-pool-1") return { lists: [], average: [] };

  const person = (memberId: string) => {
    const member = demoMemberById(memberId);
    return {
      memberId,
      displayName: member?.displayName ?? "Someone",
      avatarUrl: member?.avatarUrl ?? null,
    };
  };
  const list = (panelistId: string, order: string[], submitted: boolean): SeedingList => ({
    panelist: person(panelistId),
    submitted,
    players: order.map((id, index) => ({ ...person(id), place: index + 1 })),
    skipped: [],
  });

  const average: SeedingEntry[] = ["demo-1", "demo-2"]
    .map((id) => ({
      ...person(id),
      mean: 1.5,
      median: 1.5,
      best: 1,
      worst: 2,
      ranked: 2,
      lists: 2,
      panel: 3,
      order: 0,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((entry, index) => ({ ...entry, order: index + 1 }));

  return {
    lists: [
      list("demo-3", ["demo-2", "demo-1"], true),
      list("demo-1", ["demo-1", "demo-2"], true),
      list("demo-2", ["demo-2"], false),
    ],
    average,
  };
}

/**
 * One sample lot in the first auction pool: the pool's one bidding player (its other entry
 * captains a squad), in a lot that does not divide evenly across the two teams, so the warning
 * has something to say. The other pool has no lots yet.
 */
export function demoLots(eventId: string): PoolLots {
  if (eventId !== "demo-pool-1") return { lots: [], unassigned: [] };

  const member = demoMemberById("demo-2");
  const lot: Lot = {
    id: "demo-lot-1",
    eventId,
    position: 1,
    name: "Marquee",
    basePrice: 40,
    players: [
      {
        memberId: "demo-2",
        displayName: member?.displayName ?? "Someone",
        avatarUrl: member?.avatarUrl ?? null,
      },
    ],
  };
  return { lots: [lot], unassigned: [] };
}

/**
 * The sample auction's rules: the defaults for the tournament, and smaller steps of its own for
 * the women's pool, so the page shows both kinds of row.
 */
export function demoAuctionRules(tournamentId: string): AuctionRulesSetup | null {
  if (tournamentId !== "demo-tournament-2") return null;

  const base = {
    purseReserve: true,
    leftovers: "capped_bidding",
    unsold: "organiser",
    nextUp: "random",
    forcedPrice: "base",
    board: { rating: true, level: true, handSide: true },
  } as const;
  return {
    tournament: {
      ...base,
      slabs: [
        { from: 0, step: 5 },
        { from: 50, step: 10 },
      ],
    },
    pools: new Map([
      [
        "demo-pool-2",
        {
          ...base,
          slabs: [
            { from: 0, step: 1 },
            { from: 10, step: 2 },
          ],
        },
      ],
    ]),
  };
}

/**
 * The sample auction has not started, so each pool's state is its setup: the lots as they
 * stand, every bidding player still to offer, and the two squads with their purses whole. It
 * keeps the setup screens editable in the demo, which a started auction would freeze.
 */
export function demoAuctionState(eventId: string): AuctionPoolState | null {
  const pool = demoPools.find((p) => p.id === eventId);
  if (!pool) return null;
  const { lots } = demoLots(eventId);
  const squads = demoSquads.filter((s) => s.eventId === eventId);
  return {
    eventId,
    fill: pool.squadFill,
    started: false,
    finished: false,
    open: false,
    currentLotId: lots[0]?.id ?? null,
    up: null,
    lots: lots.map((lot) => ({
      lotId: lot.id,
      position: lot.position,
      name: lot.name,
      basePrice: lot.basePrice,
      size: lot.players.length,
      share: Math.floor(lot.players.length / Math.max(squads.length, 1)),
      leftovers: lot.players.length % Math.max(squads.length, 1),
      closed: false,
      toOffer: lot.players.length,
      toOfferAgain: 0,
    })),
    squads: squads.map((s) => ({
      squadId: s.id,
      franchiseId: s.franchiseId,
      captainId: s.captain?.id ?? null,
      purse: s.purse ?? 0,
      left: s.purse ?? 0,
      held: {},
      refusal: null,
    })),
    players: lots.flatMap((lot) =>
      lot.players.map((p) => ({
        memberId: p.memberId,
        lotId: lot.id,
        state: "offerable" as const,
        offers: 0,
        squadId: null,
        price: null,
      })),
    ),
    lastEntry: null,
  };
}

/** No pool of the sample auction is finished, so there are no teams to show. */
export function demoRosters(_eventIds: readonly string[]): RosterPlayer[] {
  return [];
}

export const demoTournaments: Tournament[] = [
  {
    id: "demo-tournament-1",
    name: "Founders Cup",
    startsOn: "2026-08-22",
    endsOn: "2026-08-23",
    venue: "Prestige Hillside Courts",
    description: "The first tournament this community has run with actual seeding.",
    createdBy: "demo-1",
    createdAt: "2026-07-01T08:00:00.000Z",
    contactRequired: false,
    auction: false,
    private: false,
    logoUrl: null,
    auctionStartedAt: null,
    auctionCurrency: "Cr",
    finishedAt: null,
    events: demoEvents,
  },
  {
    id: "demo-tournament-2",
    name: "Hillside League",
    startsOn: "2026-10-10",
    endsOn: null,
    venue: "Prestige Hillside Courts",
    description: "Teams are picked at an auction.",
    createdBy: "demo-1",
    createdAt: "2026-07-02T08:00:00.000Z",
    contactRequired: false,
    auction: true,
    private: false,
    logoUrl: null,
    auctionStartedAt: null,
    auctionCurrency: "Cr",
    finishedAt: null,
    events: demoPools,
  },
  {
    id: "demo-tournament-3",
    name: "Friday Club Night",
    startsOn: "2026-10-16",
    endsOn: null,
    venue: "Prestige Hillside Courts",
    description: "Only the people invited can see this one.",
    createdBy: "demo-1",
    createdAt: "2026-07-03T08:00:00.000Z",
    contactRequired: false,
    auction: false,
    private: true,
    logoUrl: null,
    auctionStartedAt: null,
    auctionCurrency: "Cr",
    finishedAt: null,
    events: [],
  },
];
